import type { Repo } from '../data'
import type { SnapshotGame } from '../data/snapshotTypes'
import type { Game, ID, StatEvent } from '../domain/types'

/**
 * Put local games onto the server's `games.id`, keyed on the PlayHQ fixture id.
 *
 * The same reasoning as 03.1(2) for players, and for a sharper reason: two
 * devices scoring the same fixture each minted their own uuid, so their events
 * pointed at different `stat_events.game_id` values and would never have met.
 * Nothing would have errored. The tally on the watching phones would simply
 * have been missing half the taps.
 *
 * A game with no `playhqGameId` -- one started by hand against an opponent that
 * is not in the fixture -- has no server row to borrow an id from and stays
 * local. That is a real limitation and the UI says so rather than pretending to
 * sync.
 *
 * Safe to interrupt: events are repointed BEFORE the old game row is removed,
 * so a failure half way leaves a duplicate game rather than orphaned events.
 */
export interface GameReconcileResult {
  games: Game[]
  rekeyed: number
  /** New id of the in-progress game, if it was the one that moved. */
  currentGameId: ID | null
}

export async function adoptServerGameIds(
  repo: Repo,
  games: Game[],
  fixtures: SnapshotGame[],
  currentGameId: ID | null,
): Promise<GameReconcileResult> {
  const serverIdByFixture = new Map<string, ID>()
  for (const f of fixtures) {
    if (f.playhq_game_id) serverIdByFixture.set(f.playhq_game_id, f.id)
  }

  const wrong = games.filter(
    (g) => g.playhqGameId && serverIdByFixture.get(g.playhqGameId) && serverIdByFixture.get(g.playhqGameId) !== g.id,
  )
  if (wrong.length === 0) return { games, rekeyed: 0, currentGameId }

  let allEvents: StatEvent[] | null = null
  let next = [...games]
  let movedCurrent = currentGameId

  for (const old of wrong) {
    const serverId = serverIdByFixture.get(old.playhqGameId!)!
    const collision = games.find((g) => g.id === serverId)

    allEvents = allEvents ?? (await repo.listAllEvents())
    const moved: StatEvent[] = allEvents
      .filter((e) => e.gameId === old.id)
      .map((e) => ({ ...e, gameId: serverId, updatedAt: Date.now(), syncedAt: null }))
    if (moved.length > 0) {
      await repo.saveEvents(moved)
      // Keep the in-memory copy in step, in case a second game moves too.
      const movedById = new Map(moved.map((m) => [m.id, m]))
      allEvents = allEvents.map((e) => movedById.get(e.id) ?? e)
    }

    if (!collision) {
      await repo.saveGame({ ...old, id: serverId, updatedAt: Date.now() })
      next = next.map((g) => (g.id === old.id ? { ...old, id: serverId } : g))
    } else {
      // Two local rows for one fixture. Keep the one already on the server id.
      next = next.filter((g) => g.id !== old.id)
    }
    await repo.deleteGame(old.id)

    if (movedCurrent === old.id) movedCurrent = serverId
  }

  return { games: next, rekeyed: wrong.length, currentGameId: movedCurrent }
}
